import { useCentralStore } from "../../../store/Central";
import { useState } from "react";
import { showNotification } from "../../../store/Central/selectors";
import { validateEmail } from "../../../hooks/useForm/form-field-validation-functions";
import {
	RadioGroup,
	RadioGroupItem,
} from "../../../components/ui/radio-group";
import { Button } from "../../../components/ui/button";
import { usePostHog } from "posthog-js/react";

interface NewConnectRelationshipProps {
	onConnectionSuccess?: (email: string) => void;
}

export const NewConnectRelationship: React.FC<NewConnectRelationshipProps> = ({
	onConnectionSuccess,
}) => {
	const { connectClientFromScratch } = useCentralStore((state) => ({
		connectClientFromScratch: state.connectClientFromScratch,
	}));
	const [adminEmail, setAdminEmail] = useState<string>("");
	const [connectType, setConnectType] = useState<"" | "med" | "lab">("");
	const [message, setMessage] = useState<string>(
		"Guten Tag, gerne möchte ich Sie einladen, auf dentDESK.ch zusammenzuarbeiten."
	);
	const postHog = usePostHog();
	const isConnectTypeSelected = connectType === "med" || connectType === "lab";
	const canSubmit = Boolean(adminEmail) && isConnectTypeSelected;
	const handleConnectClick = () => {
		if (!isConnectTypeSelected) {
			showNotification({
				message: "Bitte wählen Sie, ob es sich bei dem Kunden um eine Praxis oder ein Labor handelt.",
				type: "error",
			});
			return;
		}
		if (!validateEmail(adminEmail)) {
			showNotification({
				message: "Ungültige E-Mail-Adresse",
				type: "error",
			});
			return;
		}
		connectClientFromScratch(
			adminEmail,
			message,
			connectType,
			onConnectionSuccess ? () => onConnectionSuccess(adminEmail) : undefined
		);
		postHog.capture("connect_client", {
			connect_type: connectType,
		});
	};

	return (
		<div>
			<h3 className="text-sm font-medium text-gray-700 mb-2">
				Neue Verbindung anlegen
			</h3>
			<h2 className="text-sm text-gray-500 mb-4">
				Um eine neue Verbindung anzulegen, geben Sie die E-Mail-Adresse
				des Administrators der Praxis ein.
			</h2>

			<div className="mb-4">
				<span className="text-sm text-gray-700 mb-2 block">
					Verbindungs-Typ:
				</span>
				<RadioGroup
					value={connectType}
					onValueChange={(value) =>
						setConnectType((value === "med" || value === "lab") ? value : "")
					}
					className="flex items-center gap-6"
					aria-required="true"
					aria-invalid={!isConnectTypeSelected}
					aria-describedby={
						!isConnectTypeSelected ? "connect-type-help" : undefined
					}
				>
					<div className="flex items-center gap-2">
						<RadioGroupItem id="connect-type-med" value="med" />
						<label
							htmlFor="connect-type-med"
							className="text-sm text-gray-700"
						>
							Praxis
						</label>
					</div>
					<div className="flex items-center gap-2">
						<RadioGroupItem id="connect-type-lab" value="lab" />
						<label
							htmlFor="connect-type-lab"
							className="text-sm text-gray-700"
						>
							Labor
						</label>
					</div>
				</RadioGroup>
				{!isConnectTypeSelected ? (
					<p
						id="connect-type-help"
						className="text-xs text-red-600 mt-2"
					>
						Bitte einen Verbindungs-Typ auswählen.
					</p>
				) : null}
			</div>

			<div className="mb-4">
				<span className="text-sm text-gray-700 mb-2 block">
					E-Mail-Adresse des Administrators:
				</span>
				<input
					type="email"
					value={adminEmail}
					onChange={(e) => setAdminEmail(e.target.value)}
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
					placeholder={
						connectType === "med"
							? "admin@praxis.ch"
							: connectType === "lab"
								? "admin@labor.ch"
								: "admin@organisation.ch"
					}
				/>
			</div>
			<div className="mb-4">
				<span className="text-sm text-gray-700 mb-2 block">
					Nachricht:
				</span>
				<textarea
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
				/>
			</div>
			<div className="mt-4">
				<Button
					onClick={handleConnectClick}
					disabled={!canSubmit}
					variant={canSubmit ? "default" : "secondary"}
					size="sm"
				>
					Verbindung anlegen
				</Button>
			</div>
		</div>
	);
};
